/**
 * @Module: Recipe Firestore Service
 * @Status: Ready
 * @Summary: CRUD + favorites + cook tracking for user recipes in Firestore
 * @Description: Every method requires a uid — throws AUTH_REQUIRED if missing.
 *   Documents live at /users/{uid}/recipes/{recipeId}. rawText is capped at
 *   500 KB before write. All reads go through recipeFromFirestore to normalize
 *   Timestamps. create() and update() are separate to protect createdAt.
 * @NextSteps: Add batch operations, pagination for large collections
 */

import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  increment,
  query,
  orderBy,
  type Firestore,
} from 'firebase/firestore';
import type { Recipe } from '../../types/recipe';
import type { StoredRecipe } from '../../types/stored-recipe';
import { CURRENT_SCHEMA_VERSION } from '../../types/stored-recipe';
import { recipeFromFirestore } from './converters';
import { AppError, wrapFirestoreError } from './errors';

const MAX_RAW_TEXT_BYTES = 500 * 1024; // 500 KB

function assertUid(uid: string | null | undefined): asserts uid is string {
  if (!uid) throw new AppError('AUTH_REQUIRED', 'Sign in to manage recipes');
}

function recipesCol(db: Firestore, uid: string) {
  return collection(db, 'users', uid, 'recipes');
}

function recipeDoc(db: Firestore, uid: string, recipeId: string) {
  return doc(db, 'users', uid, 'recipes', recipeId);
}

function validateRawText(recipe: Partial<Recipe>): void {
  const raw = recipe.source?.rawText;
  if (raw && new Blob([raw]).size > MAX_RAW_TEXT_BYTES) {
    throw new AppError(
      'SIZE_LIMIT',
      `rawText exceeds ${MAX_RAW_TEXT_BYTES / 1024} KB limit`,
      'recipe.create',
    );
  }
}

export function createRecipeService(db: Firestore) {
  return {
    /**
     * Create a new recipe document using addDoc (auto-generated ID, single write).
     * Explicitly serializes fields to avoid sending undefined values to Firestore.
     * Ref: https://firebase.google.com/docs/firestore/manage-data/add-data
     */
    async create(uid: string | null, recipe: Recipe): Promise<StoredRecipe> {
      assertUid(uid);
      validateRawText(recipe);

      try {
        // Serialize explicitly — never spread a raw object into Firestore.
        // Firestore rejects undefined values and we don't want to store temp IDs.
        const firestoreData = {
          title: recipe.title,
          description: recipe.description || '',
          source: recipe.source ? {
            type: recipe.source.type,
            url: recipe.source.url || null,
            rawText: recipe.source.rawText || null,
            importedAt: recipe.source.importedAt || null,
          } : null,
          meta: {
            servings: recipe.meta.servings,
            prepMinutes: recipe.meta.prepMinutes || 0,
            cookMinutes: recipe.meta.cookMinutes || 0,
            totalMinutes: recipe.meta.totalMinutes || 0,
            cuisine: recipe.meta.cuisine || '',
            difficulty: recipe.meta.difficulty || 'medium',
            tags: recipe.meta.tags || [],
            unitSystem: recipe.meta.unitSystem || 'metric',
          },
          steps: recipe.steps.map(s => ({
            number: s.number,
            instruction: s.instruction,
            title: s.title || null,
            ingredients: s.ingredients.map(i => ({
              name: i.name,
              amount: i.amount,
              unit: i.unit,
              notes: i.notes || '',
            })),
            durationMinutes: s.durationMinutes ?? null,
            technique: s.technique || null,
            tip: s.tip || null,
          })),
          notes: recipe.notes || '',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          isFavorite: false,
          cookCount: 0,
          lastCookedAt: null,
          userTags: [],
          schemaVersion: CURRENT_SCHEMA_VERSION,
        };

        // addDoc generates an ID and makes a single CREATE request (no redundant UPDATE)
        const ref = await addDoc(recipesCol(db, uid), firestoreData);
        const now = new Date().toISOString();

        return {
          ...recipe,
          id: ref.id,
          createdAt: now,
          updatedAt: now,
          isFavorite: false,
          cookCount: 0,
          lastCookedAt: null,
          userTags: [],
          schemaVersion: CURRENT_SCHEMA_VERSION,
        };
      } catch (error) {
        wrapFirestoreError(error, 'recipe.create');
      }
    },

    /** Partial update — never overwrites createdAt. */
    async update(
      uid: string | null,
      recipeId: string,
      partial: Partial<Recipe>,
    ): Promise<void> {
      assertUid(uid);
      validateRawText(partial);

      try {
        const ref = recipeDoc(db, uid, recipeId);
        await updateDoc(ref, {
          ...partial,
          updatedAt: serverTimestamp(),
        });
      } catch (error) {
        wrapFirestoreError(error, 'recipe.update');
      }
    },

    /** Fetch a single recipe by ID. Throws NOT_FOUND if missing. */
    async get(uid: string | null, recipeId: string): Promise<StoredRecipe> {
      assertUid(uid);

      try {
        const snap = await getDoc(recipeDoc(db, uid, recipeId));
        if (!snap.exists()) {
          throw new AppError('NOT_FOUND', `Recipe ${recipeId} not found`);
        }
        return recipeFromFirestore(snap.id, snap.data() as Record<string, unknown>);
      } catch (error) {
        wrapFirestoreError(error, 'recipe.get');
      }
    },

    /** List all recipes for a user, newest first. */
    async list(uid: string | null): Promise<StoredRecipe[]> {
      assertUid(uid);

      try {
        const q = query(recipesCol(db, uid), orderBy('createdAt', 'desc'));
        const snap = await getDocs(q);
        return snap.docs.map((d) =>
          recipeFromFirestore(d.id, d.data() as Record<string, unknown>),
        );
      } catch (error) {
        wrapFirestoreError(error, 'recipe.list');
      }
    },

    /** Delete a recipe document permanently. */
    async delete(uid: string | null, recipeId: string): Promise<void> {
      assertUid(uid);

      try {
        await deleteDoc(recipeDoc(db, uid, recipeId));
      } catch (error) {
        wrapFirestoreError(error, 'recipe.delete');
      }
    },

    /** Toggle the isFavorite flag. Returns the new value. */
    async toggleFavorite(
      uid: string | null,
      recipeId: string,
    ): Promise<boolean> {
      assertUid(uid);

      try {
        const current = await this.get(uid, recipeId);
        const newVal = !current.isFavorite;
        await updateDoc(recipeDoc(db, uid, recipeId), {
          isFavorite: newVal,
          updatedAt: serverTimestamp(),
        });
        return newVal;
      } catch (error) {
        wrapFirestoreError(error, 'recipe.toggleFavorite');
      }
    },

    /** Bump cookCount by 1 and set lastCookedAt to now. */
    async incrementCookCount(
      uid: string | null,
      recipeId: string,
    ): Promise<void> {
      assertUid(uid);

      try {
        await updateDoc(recipeDoc(db, uid, recipeId), {
          cookCount: increment(1),
          lastCookedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      } catch (error) {
        wrapFirestoreError(error, 'recipe.incrementCookCount');
      }
    },
  };
}
